import { BrowserModule } from "@angular/platform-browser";
import { NgModule, APP_INITIALIZER, ErrorHandler } from "@angular/core";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { StoreModule } from "@ngrx/store";
import { reducers, metaReducers } from "./store/reducers";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { EffectsModule } from "@ngrx/effects";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { AppInitService } from "./services/app-init.service";
import { ClockService } from "./services/clock.service";
import { BidUpdateService } from "./services/bid-update.service";
import { AuctionsTrackingService } from "./services/auctions-tracking.service";
import { AdminService } from "./services/admin.service";
import { AuctionLayoutModule } from "./layouts/auction/auction-layout.module";
import { TokenInterceptor } from "./services/api/interceptors/token.interceptor";
import { ErrorInterceptor } from "./services/api/interceptors/error.interceptor";
import { ServerTimeInterceptor } from "./services/api/interceptors/server-time.interceptor";
import { BalanceTrackInterceptor } from "./services/api/interceptors/balance-track.interceptor";
import { UserEffects } from "../app/store/effects/userEffects";
import { BootEffects } from "./store/effects/bootEffects";
import { AdminEffects } from "./store/effects/adminEffects";
import { SharedEffects } from "./store/effects/sharedEffects";
import { InternetConnectionService } from "./services/internet-connection.service";
import { ConnectionInterceptor } from "./services/api/interceptors/connection.interceptor";
import { ModalModule } from "./modules/modal/modal.module";
import { Router, RouterModule } from "@angular/router";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import * as Sentry from "@sentry/angular";

export function initializeApp(appInitService: AppInitService) {
  return (): Promise<any> => {
    return appInitService.initilize();
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    RouterModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    AuctionLayoutModule,
    ModalModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    EffectsModule.forRoot([
      SharedEffects,
      UserEffects,
      BootEffects,
      AdminEffects,
    ]),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: true, // Restrict extension to log-only mode
    }),
  ],
  providers: [
    AppInitService,
    ClockService,
    BidUpdateService,
    AuctionsTrackingService,
    AdminService,
    InternetConnectionService,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory:
        (internetConnectionService: InternetConnectionService) => () =>
          internetConnectionService.load(),
      deps: [InternetConnectionService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppInitService],
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerTimeInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BalanceTrackInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ConnectionInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
